import React, {useEffect, useState} from "react";
import "./ExplorerFiles.scss";
import FCSpinner from "../../FCObjets/FCSpinner";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import {useFetchExplorer, DownloadExplorerArchive} from "../../../service/Business/ExplorerService";
import {getIdWithToken, getToken} from "../../../utils/storage/Token";
import {AppSettings} from "../../../AppSettings";

interface TreeNode {
    title: string;
    key: string;
    type: string;
    children?: TreeNode[];
}

const ExplorerFiles: React.FC = () => {

    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [currentPath, setCurrentPath] = useState<string[]>([]);
    const [defaultApplied, setDefaultApplied] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const {data: treeData, loading, error} = useFetchExplorer(getIdWithToken());

    // À l'arrivée des données, sélectionne par défaut le premier dossier de niveau 1
    useEffect(() => {
        if (defaultApplied || !treeData || treeData.length === 0) return;
        const firstFolder = treeData.find((n: TreeNode) => n.children);
        if (firstFolder) {
            setCurrentPath([firstFolder.title]);
            setOpenKeys([firstFolder.key]);
        }
        setDefaultApplied(true);
    }, [treeData, defaultApplied]);

    const handleDownloadArchive = () => {
        setArchiveLoading(true);
        DownloadExplorerArchive(getIdWithToken())
            .catch(() => { /* erreur silencieuse */ })
            .finally(() => setArchiveLoading(false));
    };

    const getIconName = (type: string): string => {
        switch (type) {
            case "pdf": return "picture_as_pdf";
            case "word": return "description";
            case "text": return "article";
            case "excel": return "table_chart";
            case "zip": return "folder_zip";
            case "image": return "image";
            default: return "insert_drive_file";
        }
    };

    const getIconClass = (type: string): string => {
        switch (type) {
            case "pdf": return "explorerIconPdf";
            case "word": return "explorerIconWord";
            case "excel": return "explorerIconExcel";
            case "image": return "explorerIconImage";
            case "zip": return "explorerIconZip";
            default: return "explorerIconFile";
        }
    };

    /**
     * Cherche le chemin de titres d'un nœud dans l'arbre, à partir de sa clé.
     */
    const findPathByKey = (nodes: TreeNode[], targetKey: string, trail: string[] = []): string[] | null => {
        for (const node of nodes) {
            const next = [...trail, node.title];
            if (node.key === targetKey) return next;
            if (node.children) {
                const found = findPathByKey(node.children, targetKey, next);
                if (found) return found;
            }
        }
        return null;
    };

    const handleFolderClick = (key: string) => {
        // Toggle expand/collapse
        if (openKeys.includes(key)) {
            setOpenKeys(openKeys.filter((k) => k !== key));
        } else {
            setOpenKeys([...openKeys, key]);
        }
        // Navigation : afficher le contenu du dossier dans les bulles à droite
        if (treeData) {
            const targetPath = findPathByKey(treeData, key);
            if (targetPath) setCurrentPath(targetPath);
        }
    };

    const handleFileClick = (item: TreeNode) => {
        if (item.type === "pdf") {
            window.open(`${process.env.PUBLIC_URL}/pdf/${encodeURIComponent(item.key)}`, "_blank");
        } else {
            const rawToken = getToken()?.replace("Bearer ", "") || "";
            const url = `${AppSettings.API_ENDPOINT}${AppSettings.API_FREELANCERIE}/explorer/view/${getIdWithToken().toString()}/${encodeURIComponent(item.key)}?token=${rawToken}`;
            window.open(url, "_blank");
        }
    };

    const getCurrentNodes = (): TreeNode[] => {
        if (!treeData) return [];
        let nodes = treeData;
        for (const segment of currentPath) {
            const folder = nodes.find((n: TreeNode) => n.title === segment && n.children);
            if (folder && folder.children) {
                nodes = folder.children;
            } else {
                return [];
            }
        }
        return nodes;
    };

    const navigateTo = (index: number) => {
        setCurrentPath(currentPath.slice(0, index));
    };

    const enterFolder = (folder: TreeNode) => {
        if (folder.children) {
            setCurrentPath([...currentPath, folder.title]);
        }
    };

    const isFolderActive = (item: TreeNode): boolean => {
        if (!item.children || !treeData) return false;
        const path = findPathByKey(treeData, item.key);
        if (!path || path.length !== currentPath.length) return false;
        return path.every((seg, i) => seg === currentPath[i]);
    };

    const renderTreeNodes = (data: TreeNode[], depth: number = 0) => {
        return (
            <ul className="explorerTreeList">
                {data.map((item) => {
                    const active = isFolderActive(item);
                    return (
                        <li key={item.key} className="explorerTreeItem">
                            <div className={`explorerTreeItemRow${active ? " active" : ""}`}
                                 onClick={() => item.children ? handleFolderClick(item.key) : handleFileClick(item)}>
                                <span className={`material-symbols-outlined ${item.children ? '' : getIconClass(item.type)}`}>
                                    {item.children
                                        ? (openKeys.includes(item.key) ? "folder_open" : "folder")
                                        : getIconName(item.type)}
                                </span>
                                <span className="explorerTreeItemLabel">{item.title}</span>
                            </div>
                            {item.children && openKeys.includes(item.key) && renderTreeNodes(item.children, depth + 1)}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const currentNodes = getCurrentNodes();
    const folders = currentNodes.filter((n: TreeNode) => n.children);
    const files = currentNodes.filter((n: TreeNode) => !n.children);

    return (
        <div className="explorerPage">
            {/* Header */}
            <div className="explorerHeader">
                <div>
                    <h1 className="explorerTitle">Mes fichiers</h1>
                    <p className="explorerSubtitle">Explorez vos documents professionnels</p>
                </div>
                <div className="explorerHeaderRight">
                    <button className="explorerBtnArchive"
                            onClick={handleDownloadArchive}
                            disabled={archiveLoading || !treeData || treeData.length === 0}
                            title="Télécharger l'archive ZIP de l'ensemble de votre arborescence">
                        <span className="material-symbols-outlined">
                            {archiveLoading ? "hourglass_empty" : "folder_zip"}
                        </span>
                        Archive
                    </button>
                </div>
            </div>

            {loading && <div className="explorerLoading"><FCSpinner size="middle"/></div>}
            {error && <FCAlertMsgErrorAPI error={error}/>}

            {treeData && (
                <>
                    {/* Breadcrumb */}
                    <div className="explorerBreadcrumb">
                        <span className="explorerBreadcrumbItem" onClick={() => navigateTo(0)}>
                            <span className="material-symbols-outlined" style={{fontSize: 16, verticalAlign: "middle"}}>home</span>
                        </span>
                        {currentPath.map((segment, i) => (
                            <React.Fragment key={i}>
                                <span className="explorerBreadcrumbSep">/</span>
                                {i < currentPath.length - 1 ? (
                                    <span className="explorerBreadcrumbItem" onClick={() => navigateTo(i + 1)}>
                                        {segment}
                                    </span>
                                ) : (
                                    <span className="explorerBreadcrumbCurrent">{segment}</span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="explorerContent">
                        {/* Tree sidebar (desktop) */}
                        <div className="explorerTree">
                            <h3 className="explorerTreeTitle">Arborescence</h3>
                            {renderTreeNodes(treeData)}
                        </div>

                        {/* Main content */}
                        <div className="explorerMain">
                            {/* Folders */}
                            {folders.length > 0 && (
                                <div className="explorerFileGrid" style={{marginBottom: 20}}>
                                    {folders.map((folder: TreeNode) => (
                                        <div key={folder.key} className="explorerFolderCard"
                                             onClick={() => enterFolder(folder)}>
                                            <span className="material-symbols-outlined">folder</span>
                                            <div>
                                                <div className="explorerFolderName">{folder.title}</div>
                                                <div className="explorerFolderCount">
                                                    {folder.children?.length || 0} élément{(folder.children?.length || 0) > 1 ? "s" : ""}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Files */}
                            {files.length > 0 && (
                                <div className="explorerFileGrid">
                                    {files.map((file: TreeNode) => (
                                        <div key={file.key} className="explorerFileCard"
                                             onClick={() => handleFileClick(file)}>
                                            <div className={`explorerFileIcon`}>
                                                <span className={`material-symbols-outlined ${getIconClass(file.type)}`}>
                                                    {getIconName(file.type)}
                                                </span>
                                            </div>
                                            <span className="explorerFileName">{file.title}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {folders.length === 0 && files.length === 0 && (
                                <div className="explorerEmptyState">
                                    <span className="material-symbols-outlined" style={{fontSize: 48, display: "block", marginBottom: 12, color: "var(--text-light)"}}>folder_off</span>
                                    Ce dossier est vide
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ExplorerFiles;
